<template>
  <div
    v-if="viewMode === 'list'"
    class="select-none flex items-stretch h-20 p-2 cursor-pointer rounded-[10px] bg-main-background overflow-hidden"
    data-test-id="preview-entry-list"
    @click.prevent="() => emit('openDetails', item)"
    v-long-press.prevent="() => emit('openBulkEdit', item)"
  >
    <div
      v-if="hasNoCover"
      class="shrink-0 w-10.5 h-16 flex items-center justify-center rounded-[10px] bg-main-background pointer-events-none select-none"
    >
      <IconCustom ligature="no_image" size="36" class="text-secondary-background" />
    </div>
    <img
      v-else
      :src="imageUrl"
      :draggable="false"
      :alt="`Cover for ${item.localizedSeriesTitle} - ${item.version || ''}`"
      @error="setImageLoadError(true)"
      class="shrink-0 w-10.5 h-16 object-cover rounded-[5px] pointer-events-none select-none"
    />
    <div class="flex-1 min-w-0 flex flex-col justify-center gap-2 px-2">
      <span :class="[$typo.openSans12]" class="text-text truncate"> {{ titleLine }} </span>
      <span v-if="preTitleLine" :class="[$typo.openSans10]" class="text-text-muted truncate">
        {{ preTitleLine }}
      </span>
      <span v-if="postTitleLine" :class="[$typo.openSans10]" class="text-text-muted truncate">
        {{ postTitleLine }}
      </span>
    </div>
    <div class="flex flex-col items-center gap-0.5 shrink-0 self-start">
      <IconButton
        v-if="showAddToCollection"
        background-class="bg-transparent"
        @click.stop="emit('addToCollection', item)"
      >
        <IconCustom class="text-text-muted" ligature="copy_plus" size="16" />
      </IconButton>
      <PreviewEntryStatusButton
        v-if="showOwnedStatusIcon"
        background-class="bg-transparent"
        :class="showVolumesRead ? 'w-15 justify-end pr-1.5' : ''"
        icon-color-class="text-text-muted"
        icon-position="right"
        :icon-ligature="ownedIconLigature"
        :count="collectionState.volumesOwned"
        :volume-count="volumeCount"
        :show-count="showVolumesOwned"
        @click.stop="handleOwnedClick"
      />
      <PreviewEntryStatusButton
        v-if="showReadStatusIcon"
        background-class="bg-transparent"
        :class="showVolumesRead ? 'w-15 justify-end pr-1.5' : ''"
        icon-color-class="text-text-muted"
        icon-position="right"
        :icon-ligature="readIconLigature"
        :count="collectionState.volumesRead"
        :volume-count="volumeCount"
        :show-count="showVolumesRead"
        @click.stop="handleReadClick"
      />
    </div>
  </div>
  <div
    v-else-if="viewMode === 'compact'"
    class="select-none flex items-center gap-2.5 px-3.75 h-12.5 cursor-pointer rounded-[10px] bg-main-background min-w-0 overflow-hidden"
    data-test-id="preview-entry-compact"
    @click.prevent="() => emit('openDetails', item)"
    @mouseup.middle.prevent="() => emit('openDetailsNewTab', item)"
    v-long-press.prevent="() => emit('openBulkEdit', item)"
  >
    <div class="flex-1 min-w-0 flex flex-col">
      <span :class="[$typo.openSans12]" class="text-text truncate">
        {{ titleLine }}
      </span>
      <span
        v-if="preTitleLine || postTitleLine"
        :class="[$typo.openSans10]"
        class="text-text-muted truncate"
      >
        {{ [preTitleLine, postTitleLine].filter(Boolean).join(' · ') }}
      </span>
    </div>
    <div class="flex items-center gap-1 shrink-0">
      <IconButton
        v-if="showAddToCollection"
        background-class="bg-transparent w-10"
        @click.stop="emit('addToCollection', item)"
      >
        <IconCustom class="text-text-muted" ligature="copy_plus" size="16" />
      </IconButton>
      <PreviewEntryStatusButton
        v-if="showOwnedStatusIcon"
        background-class="bg-transparent"
        icon-color-class="text-text-muted"
        orientation="vertical"
        :icon-ligature="ownedIconLigature"
        :count="collectionState.volumesOwned"
        :volume-count="volumeCount"
        :show-count="showVolumesOwned"
        @click.stop="handleOwnedClick"
      />
      <PreviewEntryStatusButton
        v-if="showReadStatusIcon"
        background-class="bg-transparent"
        icon-color-class="text-text-muted"
        orientation="vertical"
        :icon-ligature="readIconLigature"
        :count="collectionState.volumesRead"
        :volume-count="volumeCount"
        :show-count="showVolumesRead"
        @click.stop="handleReadClick"
      />
    </div>
  </div>
  <div
    v-else
    class="select-none relative overflow-visible"
    :style="{
      width: `${imageDimensions.width}px`,
      minHeight: `${imageDimensions.entryHeight}px`,
      maxHeight: `${imageDimensions.entryHeight}px`,
      ...(fixedHeight ? {} : { marginBottom: 'var(--release-item-gap-vertical)' }),
    }"
    data-test-id="preview-entry"
    :data-entry-title="item.localizedSeriesTitle"
    :data-entry-id="item.entryId"
    :data-release-date="item.releaseDate"
  >
    <div class="flex flex-col" :style="{ width: `${imageDimensions.width}px` }">
      <div
        class="relative flex"
        :style="{
          minHeight: `${imageDimensions.height}px`,
          maxHeight: `${imageDimensions.height}px`,
        }"
      >
        <div
          @click.prevent="() => emit('openDetails', item)"
          @mouseup.middle.prevent="() => emit('openDetailsNewTab', item)"
          v-long-press.prevent="() => emit('openBulkEdit', item)"
        >
          <div
            v-if="hasNoCover"
            class="flex items-center justify-center bg-main-background rounded-[10px] pointer-events-none select-none"
            :style="{
              minHeight: `${imageDimensions.height}px`,
              maxHeight: `${imageDimensions.height}px`,
              minWidth: `${imageDimensions.width}px`,
              maxWidth: `${imageDimensions.width}px`,
            }"
          >
            <IconCustom ligature="no_image" size="72" class="text-secondary-background" />
          </div>
          <div
            v-else
            class="@container relative flex-1 w-full overflow-hidden rounded-[10px]"
            :style="{
              minHeight: `${imageDimensions.height}px`,
              maxHeight: `${imageDimensions.height}px`,
              minWidth: `${imageDimensions.width}px`,
              maxWidth: `${imageDimensions.width}px`,
            }"
          >
            <!-- thumbHash blur-up placeholder, always behind the cover -->
            <div
              class="absolute inset-0"
              :style="{
                backgroundImage: `url(&quot;${coverPlaceholder}&quot;)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }"
            />
            <img
              ref="coverImageElement"
              :src="imageUrl"
              :draggable="false"
              :alt="`Cover for ${item.localizedSeriesTitle} - ${item.version || ''}`"
              @load="onCoverLoad"
              @error="setImageLoadError(true)"
              class="absolute inset-0 w-full h-full object-cover pointer-events-none select-none transition-opacity duration-500"
              :class="{
                '@min-[130px]:blur-[2px]': isLowResSource,
                'opacity-0': !coverLoaded,
                'opacity-100': coverLoaded,
              }"
            />
          </div>
        </div>

        <IconButton
          v-if="showAddToCollection"
          data-test-id="preview-entry-add"
          class="absolute right-2.5 top-2.5"
          @click="emit('addToCollection', item)"
        >
          <IconCustom class="text-text-on-dark" ligature="copy_plus" size="16"></IconCustom>
        </IconButton>

        <PreviewEntryStatusButton
          v-if="showReadStatusIcon"
          data-test-id="preview-entry-read"
          class="absolute right-2.5 top-2.5"
          :class="showVolumesRead ? 'w-auto px-1.5' : ''"
          :icon-ligature="readIconLigature"
          :count="collectionState.volumesRead"
          :volume-count="volumeCount"
          :show-count="showVolumesRead"
          @click="handleReadClick"
        />

        <PreviewEntryStatusButton
          v-if="showOwnedStatusIcon"
          data-test-id="preview-entry-owned"
          class="absolute left-2.5 top-2.5"
          :class="showVolumesRead ? 'w-auto px-1.5' : ''"
          :icon-ligature="ownedIconLigature"
          :count="collectionState.volumesOwned"
          :volume-count="volumeCount"
          :show-count="showVolumesOwned"
          @click="handleOwnedClick"
        />

        <div
          class="absolute rounded-b-[10px] -bottom-px -left-px -right-px py-2.5 px-1.25 flex flex-col bg-transparent-background text-text-on-dark pointer-events-none overflow-hidden"
        >
          <div
            v-if="preTitleLine"
            :class="[$typo.openSans10]"
            class="text-text-on-dark-muted text-center select-none mb-0.75"
          >
            {{ preTitleLine }}
          </div>
          <div
            :class="[
              fixedHeight ? $typo.openSansBold10 : $typo.openSansBold14,
              fixedHeight ? 'mb-px' : 'mb-1.25',
            ]"
            class="text-center line-clamp-2 select-none text-balance w-full text-text-on-dark"
          >
            {{ titleLine }}
          </div>
          <div
            v-if="postTitleLine"
            :class="[$typo.openSans10]"
            class="text-text-on-dark-muted text-center select-none text-balance"
          >
            {{ postTitleLine }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { useThemeStore } from '@/stores/theme.ts'
import { storeToRefs } from 'pinia'
import { useUserCollectionStore } from '@/stores/user-collection'
import IconCustom from '@/components/IconCustom.vue'
import IconButton from '@/components/IconButton.vue'
import vLongPress from '@/directives/long-press.ts'
import PreviewEntryStatusButton from '@/components/PreviewEntryStatusButton.vue'
import { CompatibleAccuracyType, getVolumeNumberFromVersion } from '@/utils'
import $typo from '@/styles/typografy.module.css'
import { NoveltyRelationTypeType, SeriesInfoTypeType } from '../../__generated__/graphql.ts'
import type { ListViewMode } from '@/stores/preferences.ts'
import { match, P } from 'ts-pattern'
import { isLowResCover } from '@/utils/lowResCover.ts'
import type { IconName } from '@/icons'
import { LocalizationStore, useLocalizationStore } from '@/stores/localization.ts'
import { useCoverSrc } from '@/utils/useCoverSrc.ts'

export interface PreviewEntryData {
  id: string
  releaseDate?: string
  releaseDateAccuracy?: CompatibleAccuracyType
  version?: string
  number?: number
  originalNumber?: number
  name?: string
  price?: number
  volumeCount?: number
  totalVolumeCount?: number
  type?: SeriesInfoTypeType
  entryId: string
  releaseComplete?: boolean
  cover: string
  originalCover?: string
  thumbHash?: string
  localizedSeriesTitle: string
  publisherName: string
  relationType?: NoveltyRelationTypeType
  relationId: string
  collectionVolumes?: string[]
  collectionSpansEntries?: boolean
  collectionVolumeCount?: number
}

export type LineBuilderConfig = {
  preTitleLine: (item: PreviewEntryData, localizationStore: LocalizationStore) => string
  titleLine: (item: PreviewEntryData, localizationStore: LocalizationStore) => string
  postTitleLine: (item: PreviewEntryData, localizationStore: LocalizationStore) => string
}

interface Props {
  item: PreviewEntryData
  windowWidth: number
  lineBuilder: LineBuilderConfig
  fixedHeight?: number
  hideAddToCollection?: boolean
  viewMode?: ListViewMode
  noveltyAddMode?: boolean
}

const props = defineProps<Props>()
const localizationStore = useLocalizationStore()

const emit = defineEmits<{
  (e: 'addToCollection', item: PreviewEntryData): void
  (e: 'toggleRead', item: PreviewEntryData, read: boolean): void
  (e: 'toggleOwned', item: PreviewEntryData, owned: boolean): void
  (e: 'increaseRead', item: PreviewEntryData): void
  (e: 'increaseOwned', item: PreviewEntryData): void
  (e: 'openBulkEdit', item: PreviewEntryData): void
  (e: 'openDetails', item: PreviewEntryData): void
  (e: 'openDetailsNewTab', item: PreviewEntryData): void
}>()

const { currentTheme } = storeToRefs(useThemeStore())
const { collectionMap } = storeToRefs(useUserCollectionStore())
const { item, lineBuilder, fixedHeight } = toRefs(props)
const imageLoadError = ref(false)

const responsive = (width: number, xs: number, sm: number, md: number, lg: number): number => {
  if (width < 576) return xs
  if (width < 768) return sm
  if (width < 992) return md
  return lg
}

const calculateImageDimensions = (wWidth: number) => {
  if (props.fixedHeight) {
    const height = props.fixedHeight
    const width = Math.round((height * 2) / 3)
    return { width, height, entryHeight: height }
  }
  const columns = responsive(wWidth, 2, 2, 3, 5)
  const width = Math.floor(
    (wWidth -
      currentTheme.value.layout.appPadding * 2 -
      (columns - 1) * currentTheme.value.layout.releaseItemGapHorizontal) /
      columns,
  )
  const height = (width * 3) / 2
  return {
    width,
    height,
    entryHeight: Math.round(height),
  }
}

const imageDimensions = ref(calculateImageDimensions(props.windowWidth))

watch(
  () => props.windowWidth,
  (newWidth) => {
    imageDimensions.value = calculateImageDimensions(newWidth)
  },
)

watch(
  () => currentTheme.value,
  () => {
    imageDimensions.value = calculateImageDimensions(props.windowWidth)
  },
)

const collectionState = computed(() => {
  return {
    volumesRead: collectionMap.value?.[item.value.entryId]?.readVolumes,
    volumesOwned: collectionMap.value?.[item.value.entryId]?.ownedVolumes,
    latestOwnedVolumeNumber: collectionMap.value?.[item.value.entryId]?.ownedMap
      ? Object.entries(collectionMap.value?.[item.value.entryId]?.ownedMap)
          .filter(([_, ownedCount]) => ownedCount > 0)
          .map(([version]) => version)
          .map(getVolumeNumberFromVersion)
          .map(Number)
          .sort((a, b) => a - b)
          .pop() || 0
      : 0,
    volumeReadStatus:
      !!collectionMap.value?.[item.value.entryId]?.latestReadMap?.[
        props?.item?.version ? getVolumeNumberFromVersion(props.item.version) : '0'
      ],
    volumeOwnedStatus:
      collectionMap.value?.[item.value.entryId]?.ownedMap?.[props?.item?.version || '0'] > 0,
    collectingRelease: !!collectionMap.value?.[item.value.entryId],
    itemOwned:
      (collectionMap.value?.[item.value.entryId]?.miscOwnedMap?.[props?.item?.relationId || '0']
        ?.quantity || 0) > 0,
  }
})

const volumeCount = computed(() => {
  if (item.value.volumeCount !== undefined) {
    if (
      Number.isInteger(collectionState.value.latestOwnedVolumeNumber) &&
      collectionState.value.latestOwnedVolumeNumber > item.value.volumeCount
    ) {
      return collectionState.value.latestOwnedVolumeNumber
    }
    return item.value.volumeCount
  }
  return undefined
})

const preTitleLine = computed(() => {
  return lineBuilder.value.preTitleLine(item.value, localizationStore)
})

const titleLine = computed(() => {
  return lineBuilder.value.titleLine(item.value, localizationStore)
})

const postTitleLine = computed(() => {
  return lineBuilder.value.postTitleLine(item.value, localizationStore)
})

const hasNoCover = computed(() => {
  if (imageLoadError.value || (!item.value.cover && !item.value.originalCover)) {
    return true
  }
  const coverName = (
    (item.value.cover.trim().length > 0 ? item.value.cover : item.value.originalCover) || ''
  )
    .split('.')[0]
    .trim()
  return !coverName
})

const { src: resolvedCoverSrc, placeholder: coverPlaceholder } = useCoverSrc(
  () => item.value.cover,
  () => item.value.originalCover,
  () => item.value.thumbHash,
)

const imageUrl = computed(() => (hasNoCover.value ? '' : resolvedCoverSrc.value))

const coverImageElement = ref<HTMLImageElement | null>(null)
const isLowResSource = ref(false)
// Fade the cover in over the thumbHash blur once it loads. Reset on src change so
// a swapped cover fades again instead of popping.
const coverLoaded = ref(false)
watch(imageUrl, () => {
  isLowResSource.value = false
  coverLoaded.value = false
})

function onCoverLoad() {
  isLowResSource.value = isLowResCover(coverImageElement.value, imageUrl.value)
  coverLoaded.value = true
}

// A cached cover can be `complete` before @load binds — reveal it (else it stays
// faded out at opacity 0).
onMounted(() => {
  const el = coverImageElement.value
  if (el?.complete && el.naturalWidth > 0) onCoverLoad()
})

const setImageLoadError = (value: boolean) => {
  imageLoadError.value = value
}

const showVolumesRead = computed(() =>
  match(props.item.relationType)
    .with(
      NoveltyRelationTypeType.Volume,
      P.nullish,
      () => Number.isInteger(collectionState.value.volumesRead) && volumeCount.value !== undefined,
    )
    .with(NoveltyRelationTypeType.Collection, () => false)
    .with(NoveltyRelationTypeType.Extra, () => false)
    .exhaustive(),
)

const showVolumesOwned = computed(() =>
  match(props.item.relationType)
    .with(
      NoveltyRelationTypeType.Volume,
      P.nullish,
      () => Number.isInteger(collectionState.value.volumesOwned) && volumeCount.value !== undefined,
    )
    .with(NoveltyRelationTypeType.Collection, () => false)
    .with(NoveltyRelationTypeType.Extra, () => false)
    .exhaustive(),
)

const collectionSpansMultipleEntries = computed(() => !!item.value.collectionSpansEntries)

const showAddToCollection = computed(
  () =>
    !props.hideAddToCollection &&
    !fixedHeight.value &&
    match(props.item.relationType)
      .with(NoveltyRelationTypeType.Volume, P.nullish, () =>
        props.noveltyAddMode
          ? !collectionState.value.collectingRelease
          : !Number.isInteger(collectionState.value.volumesRead) &&
            !Number.isInteger(collectionState.value.volumesOwned),
      )
      .with(NoveltyRelationTypeType.Collection, () =>
        collectionSpansMultipleEntries.value
          ? !collectionState.value.itemOwned
          : !collectionState.value.collectingRelease,
      )
      .with(NoveltyRelationTypeType.Extra, () => false)
      .exhaustive(),
)

const showReadStatusIcon = computed(
  () =>
    !fixedHeight.value &&
    match(props.item.relationType)
      .with(
        NoveltyRelationTypeType.Volume,
        P.nullish,
        () =>
          Number.isInteger(collectionState.value.volumesRead) ||
          collectionState.value.volumeReadStatus,
      )
      .with(NoveltyRelationTypeType.Collection, () => false)
      .with(NoveltyRelationTypeType.Extra, () => false)
      .exhaustive(),
)

const showOwnedStatusIcon = computed(
  () =>
    !fixedHeight.value &&
    match(props.item.relationType)
      .with(NoveltyRelationTypeType.Volume, P.nullish, () =>
        props.noveltyAddMode
          ? collectionState.value.collectingRelease || collectionState.value.volumeOwnedStatus
          : Number.isInteger(collectionState.value.volumesOwned) ||
            collectionState.value.volumeOwnedStatus,
      )
      .with(NoveltyRelationTypeType.Collection, () =>
        collectionSpansMultipleEntries.value
          ? collectionState.value.itemOwned
          : collectionState.value.collectingRelease,
      )
      .with(NoveltyRelationTypeType.Extra, () => false)
      .exhaustive(),
)

const readIconLigature = computed<IconName>(() => {
  if (volumeCount.value !== undefined && volumeCount.value > collectionState.value.volumesRead) {
    return 'eye_plus'
  }
  return item.value.version === undefined || collectionState.value.volumeReadStatus
    ? 'eye'
    : 'eye_closed'
})

const ownedIconLigature = computed<IconName>(() =>
  match(item.value.relationType)
    .with(NoveltyRelationTypeType.Volume, P.nullish, () => {
      if (
        volumeCount.value !== undefined &&
        volumeCount.value > collectionState.value.volumesOwned
      ) {
        return 'library_plus'
      }
      return item.value.version === undefined || collectionState.value.volumeOwnedStatus
        ? ('square_library' as IconName)
        : ('square' as IconName)
    })
    .with(NoveltyRelationTypeType.Collection, () =>
      collectionState.value.itemOwned ? ('square_library' as IconName) : ('square' as IconName),
    )
    .with(NoveltyRelationTypeType.Extra, () => 'square_library' as IconName)
    .exhaustive(),
)

const handleReadClick = () => {
  if (item.value.version !== undefined) {
    emit('toggleRead', item.value, collectionState.value.volumeReadStatus)
  } else if (volumeCount.value && collectionState.value.volumesRead < volumeCount.value) {
    emit('increaseRead', item.value)
  }
}

const handleOwnedClick = () => {
  match(props.item.relationType)
    .with(NoveltyRelationTypeType.Volume, P.nullish, () => {
      if (item.value.version !== undefined) {
        emit('toggleOwned', item.value, collectionState.value.volumeOwnedStatus)
      } else if (volumeCount.value && collectionState.value.volumesOwned < volumeCount.value) {
        emit('increaseOwned', item.value)
      }
    })
    .with(NoveltyRelationTypeType.Collection, () => {
      emit('toggleOwned', item.value, collectionState.value.itemOwned)
    })
    .with(NoveltyRelationTypeType.Extra, () => {
      // Do nothing for extras
    })
    .exhaustive()
}
</script>
